import type { components } from '@tallerya/api-client';
import { useTraduccion } from '@tallerya/i18n';
import { PanelLateral } from '@tallerya/ui';
import { textoDeAdjunto, textoDeEntrada, type Campos } from '../../componentes/historial/cambios';
import { LineaDeTiempo } from '../../componentes/historial/LineaDeTiempo';
import { leerDetalle, nombreDe } from './CamposDelTipo';
import { useHistorialActivo } from './datos';

// El panel «Historial» de la ficha del activo (#187), con la línea de tiempo
// común. Lo de acá es cómo se lee cada cambio:
//
// - **La placa, el código y las notas no dicen a qué cambiaron**: la placa se
//   asocia a su dueño, y la auditoría nunca los guardó. Los detalles de texto
//   tampoco. Se juntan en «Cambió: placa, notas».
// - **El cliente, la marca y el modelo llegan por su nombre**, y el año tal cual.
// - **Los detalles se leen con los campos del tipo**, igual que en la ficha:
//   «Kilometraje: 84.500 → 90.000». Por eso el panel espera al tipo; si no se
//   pudo leer, sale igual, con la clave de cada detalle.
// - **Las fotos y los archivos, sin su nombre** (#195): «Foto agregada», o
//   «Archivo agregado» si es un PDF.

type Tipo = components['schemas']['AssetTypeDetail'];

export function PanelHistorial({
  activoId,
  tipo,
  abierto,
  onCerrar,
}: {
  activoId: string;
  /** Sin definir mientras carga; nulo si no se pudo leer. */
  tipo: Tipo | null | undefined;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const { t, idioma } = useTraduccion();
  const consulta = useHistorialActivo(activoId, abierto);

  const campos: Campos = {
    code: { etiqueta: t('admin.activos.codigo') },
    // «Placa», «Serie»: como lo llama su tipo.
    identifier: { etiqueta: tipo?.identifierLabel ?? t('admin.activos.identificador') },
    year: { etiqueta: t('admin.activos.anio') },
    customer_id: { etiqueta: t('admin.activos.cliente'), vacio: t('admin.activos.ficha.sinCliente') },
    brand_id: { etiqueta: t('admin.activos.marca'), vacio: t('admin.activos.sinMarca') },
    model_id: { etiqueta: t('admin.activos.modelo'), vacio: t('admin.activos.sinModelo') },
    notes: { etiqueta: t('admin.activos.notas') },
    attachment_added: { frase: (cambio) => textoDeAdjunto(t, cambio.to, true) },
    attachment_removed: { frase: (cambio) => textoDeAdjunto(t, cambio.to, false) },
  };

  // Todos los campos del tipo, también los apagados: lo que se cambió antes de
  // apagarlo sigue siendo su historia.
  for (const campo of tipo?.fields ?? []) {
    campos[nombreDe(campo.code)] = { etiqueta: campo.label, valor: (texto) => leerDetalle(campo, texto, t, idioma) };
  }

  return (
    <PanelLateral abierto={abierto} onCerrar={onCerrar} titulo={t('admin.historial.titulo')} info={t('admin.activos.historial.info')}>
      {tipo === undefined ? (
        <p className="ficha__nota">{t('app.cargando')}</p>
      ) : (
        <LineaDeTiempo consulta={consulta} texto={(entrada) => textoDeEntrada(entrada, campos, t)} />
      )}
    </PanelLateral>
  );
}
